import React from 'react';
import { Field } from '@ark-ui/react/field';
import clsx from 'clsx';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export type TextInputType =
  | 'text'
  | 'email'
  | 'password'
  | 'tel'
  | 'url'
  | 'search'
  | 'number'
  | 'date';

export interface TextInputProps extends BaseInputProps<string> {
  type?: TextInputType;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  className?: string;
}

/**
 * Atomický textový input postavený na Ark UI `Field`.
 *
 * `Field.Root` propaguje stavy `invalid`/`disabled` přes Ark `data-*` atributy,
 * zatímco všechny QA atributy ({@link BaseInputProps.testAttributes}) se
 * propisují přímo na nativní `<input>`. `id` přichází z `<FormField />`
 * (generuje se přes `React.useId()`), aby fungovala asociace s
 * `<label htmlFor>`.
 */
export const TextInput: React.FC<TextInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  required,
  placeholder,
  describedBy,
  testAttributes,
  type = 'text',
  inputMode,
  className,
}) => {
  const defaultInputMode =
    type === 'email'
      ? 'email'
      : type === 'number'
        ? 'decimal'
        : type === 'tel'
          ? 'tel'
          : undefined;

  return (
    <Field.Root
      invalid={hasError}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
    >
      <Field.Input
        id={id}
        ref={ref}
        name={name}
        type={type}
        inputMode={inputMode ?? defaultInputMode}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onChange(event.target.value)
        }
        onBlur={onBlur}
        className={clsx(styles.input, className)}
        aria-describedby={describedBy}
        {...testAttributes}
      />
    </Field.Root>
  );
};

TextInput.displayName = 'TextInput';
